import { useRef } from 'react'

import { AIEmojisAPI } from '@makyoapp/makyo-sdk'
import { IconPickerProps } from '@makyoapp/makyo-tw-ui'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { useCompBackgroundColor } from '@/context/__project__/CompContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'

import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'

interface CompIconPickerWrapperCoreProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  children: (props: IconPickerProps, ref: React.RefObject<HTMLInputElement>) => React.ReactNode
}

export function CompIconPickerWrapperCore({
  infos: { settings, containerClient, detailTableId, detailRowIndex, component },
  form,
  children,
}: CompIconPickerWrapperCoreProps) {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()
  const { currentParentBg } = useCompBackgroundColor()

  // ========================= form settings =========================
  const { formState, setFormState } = form

  // ========================= data =========================
  const columnId = settings[ComponentSettingKey.IconPickerDataValue] as string

  const isEditable = getSafely(settings, [ComponentSettingKey.TextFieldDataDefaultValueEditable], true) as boolean

  // ========================= behavior =========================
  const required = settings?.[ComponentSettingKey.IconPickerBehaviorRequired] as boolean

  // ========================= form state =========================
  const value = formState[columnId] as string | number | undefined
  const inputRef = useRef<HTMLInputElement>(null)

  const labelData = settings[ComponentSettingKey.IconPickerDataLabel]
  const helpData = settings[ComponentSettingKey.IconPickerDataHelp]
  const label = useParseVariableText(labelData, containerClient, detailTableId, detailRowIndex)
  const help = useParseVariableText(helpData, containerClient, detailTableId, detailRowIndex)

  const filterIcons = async (keyword: string) => {
    return await AIEmojisAPI.ReqFindEmoji(keyword)
  }

  const iconPickerFieldProps: IconPickerProps = {
    value: value as string,
    onChange: (value: string) => setFormState((prev) => ({ ...prev, [columnId]: value })),
    label,
    supportingText: help,
    required,
    disabled: !isEditable,
    backgroundColor: currentParentBg,
    customSearchFilter: filterIcons as any,
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(iconPickerFieldProps, inputRef)}
        <CompOverlayDevmode handleClick={() => openComponentSettings(component)} />
      </div>
    )
  }

  return children(iconPickerFieldProps, inputRef)
}
